import { useEffect, useState } from 'react';

import { IRoundScoresStore } from '../../../../store/admin/roundScores/store';
import { IGame } from '../../../../store/player/games/store';

import { IAdminEventRoundsStore, IRoundGame } from '../../../../store/admin/eventRounds/store';

interface IUseSummaryStateLogic {
  clubId: number;
  eventId: number;
  roundId: number;
  rounds: IAdminEventRoundsStore;
  roundScores: IRoundScoresStore;
  saveRoundScores: any;
  updateGames: any;
  playerId: number;
  updateRound: (newRoundId: string) => void;
}

export const useSummaryStateLogic = ({
  clubId,
  eventId,
  roundId,
  rounds,
  roundScores,
  saveRoundScores,
  updateGames,
  updateRound,
  playerId
}: IUseSummaryStateLogic) => {
  const [player, setPlayer] = useState<any>();
  const [editMode, setEditMode] = useState(false);
  const [newScores, setNewScores] = useState<number[]>([]);
  const [signed, setSigned] = useState(false);
  const [penalties, setPenalties] = useState(0);
  const currentRound = rounds[Number(roundId)];
  const enrollments = currentRound ? currentRound.enrollments : [];

  const games =
    currentRound && currentRound.groups
      ? currentRound.groups.reduce(
          (gameList: IRoundGame[], group) => [...gameList, ...group.games],
          []
        )
      : [];

  const [game, setGame] = useState<IGame | undefined>();

  useEffect(() => {
    setGame(games.find(g => g.enrollment === (player ? player.id : 0)) as any);
  }, [games, player]);

  useEffect(() => {
    if (game) {
      setPenalties(game.penalties || 0);
      setSigned(game.status === 'confirmed');
    }
  }, [game]);

  const onChangeRound = (newRoundId: string) => () => updateRound(newRoundId);

  const onToggleEdit = () => setEditMode(!editMode);

  const onCancelEdit = () => {
    if (game) {
      if (penalties !== game.penalties) {
        setPenalties(game.penalties || 0);
      }
      if (signed !== (game.status === 'confirmed')) {
        setSigned(game.status === 'confirmed');
      }
    }
    setEditMode(false);
  };

  const scores = roundScores[Number(roundId)] || [];

  const selectedScores = game
    ? {
        ...game,
        grossScore: game.grossScore || 0,
        group: game.group.id,
        handicap: game.handicap || 0,
        round: game.round.id,
        scores: scores.filter(r => r.game === game.id)
      }
    : undefined;

  const onSave = () => {
    if (game) {
      const currentScores = (selectedScores ? selectedScores.scores : [])
        .map((s, index) =>
          !!s.id
            ? {
                gross: newScores[index],
                id: s.id
              }
            : {
                game: game.id,
                gross: newScores[index],
                order: s.order
              }
        )
        .filter(f => !!f.gross);

      saveRoundScores(clubId, eventId, roundId, {
        instances:
          currentScores.length > 0
            ? currentScores
            : newScores.map(score => ({
                game: game.id,
                gross: score
              }))
      });
      if (game.signature !== signed || game.penalties !== penalties) {
        updateGames(clubId, eventId, roundId, [
          {
            ...game,
            penalties,
            status: signed ? 'confirmed' : 'draft'
          }
        ]);
      }
    }
    setEditMode(false);
  };

  useEffect(() => {
    const newPlayer = enrollments.find(e => e.id === playerId);
    setPlayer(newPlayer);
  }, [enrollments, roundId]);

  useEffect(() => {
    if ((!player || playerId !== player.id) && enrollments.length > 0) {
      const newPlayer = (enrollments || []).find(e => e.id === playerId);

      if (newPlayer) {
        setPlayer(newPlayer);
      }
    }
  }, [playerId, roundId]);

  return {
    currentRound,
    editMode,
    enrollments,
    onCancelEdit,
    onChangeRound,
    onSave,
    onToggleEdit,
    penalties,
    player,
    selectedScores,
    setEditMode,
    setNewScores,
    setPenalties,
    setPlayer
  };
};
